import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { useCallback } from "react";
import useSWR, { SWRConfiguration } from "swr";

import { logApiError, logApiRequest, logApiResponse } from "../../common/helpers/apiLogger";
import generalRequestErrorHandler from "./generalRequestErrorHandler";

interface IApiRequestPostConfig<TResponse, TRequest = any> {
	url: string | null;
	axiosConfig?: AxiosRequestConfig;
	swrConfig?: SWRConfiguration<{ data: TResponse; status: number }, AxiosError>;
	onError?: (error: AxiosError | Error) => void;
	body?: TRequest;
	triggerOnMount?: boolean;
}

interface IApiRequestPostResult<TResponse> {
	data: TResponse | undefined;
	status: number | undefined;
	isLoading: boolean;
	isValidating: boolean;
	error: AxiosError | undefined;
	getUpdateData: ReturnType<
		typeof useSWR<{ data: TResponse; status: number }, AxiosError>
	>["mutate"];
	postData: (
		overrideBody?: any,
		overrideConfig?: AxiosRequestConfig
	) => Promise<{ data: TResponse; status: number } | undefined>;
}

const poster = async <T>(
	url: string,
	body?: any,
	config?: AxiosRequestConfig
): Promise<{ data: T; status: number }> => {
	const startTime = Date.now();

	// Log the request
	logApiRequest({
		url,
		method: "POST",
		headers: config?.headers,
		body,
	});

	try {
		const response = await axios.post<T>(url, body, config);
		const duration = Date.now() - startTime;

		// Log successful response
		logApiResponse({
			url,
			method: "POST",
			status: response.status,
			responseData: response.data,
			duration,
		});

		return { data: response.data, status: response.status };
	} catch (error: any) {
		const duration = Date.now() - startTime;

		// Log error response
		logApiError({
			url,
			method: "POST",
			status: error.response?.status,
			error: error.response?.data || error.message,
			duration,
		});

		generalRequestErrorHandler(error as AxiosError);
		throw error;
	}
};

const useApiRequestPost = <TResponse = any, TRequest = any>(
	config: IApiRequestPostConfig<TResponse, TRequest>
): IApiRequestPostResult<TResponse> => {
	const { triggerOnMount = false, ...restConfig } = config;

	const { data, error, mutate, isValidating, isLoading } = useSWR<
		{ data: TResponse; status: number },
		AxiosError
	>(
		restConfig.url,

		triggerOnMount
			? (url) => poster<TResponse>(url, restConfig.body, restConfig.axiosConfig)
			: null,
		{
			...restConfig.swrConfig,
			revalidateOnMount: triggerOnMount,
			shouldRetryOnError: false, // Disable automatic retries
			onError: (err) => {
				console.log("🔍 SWR onError triggered:", err);
				if (restConfig.onError) {
					restConfig.onError(err);
				}
			},
		}
	);

	const postData = useCallback(
		async (
			overrideBody?: any,
			overrideConfig?: AxiosRequestConfig
		): Promise<{ data: TResponse; status: number } | undefined> => {
			if (!config.url) {
				return undefined;
			}

			try {
				const promise = poster<TResponse>(config.url, overrideBody ?? config.body, {
					...config.axiosConfig,
					...overrideConfig,
				});

				// Use mutate with both success and error handling
				const result = await mutate(promise, {
					revalidate: false,
					throwOnError: true, // This ensures errors are thrown
				});

				return result;
			} catch (err) {
				console.log("🔍 PostData error caught:", err);
				// Re-throw to ensure the error propagates correctly
				throw err;
			}
		},
		[config.url, config.body, config.axiosConfig, mutate]
	);

	return {
		data: data?.data,
		status: data?.status,
		isLoading,
		isValidating,
		error,
		getUpdateData: mutate,
		postData,
	};
};

export default useApiRequestPost;
